import * as log from 'loglevel';
import {html, render} from 'lit-html';
import {classMap} from 'lit-html/directives/class-map';

import SavedConfigs from './saved-configs';
import {CartItemProperties, ThemeConfig} from '../const';
import {icons, buttonClassMap} from '../options/save-config.option';

const t = SavedConfigs.t;

// save for later of the cart: every line becomes its own saved configuration, in one request
// a line not configured with the app is saved without a configuration: restored to the cart only, never opened on the product page
// placeholders: the cart app block ( style see cart-save.liquid ) or injected above the checkout buttons ( ThemeConfig.cartSaveBtn )
class CartSave {
    // settings: { api, assets, shop }
    constructor(el, settings, shopifyCart) {
        this.el = el;
        this.shopifyCart = shopifyCart;
        this.loading = false;
        this.status = '';
        this.saved = false; // the last save succeeded: the status tells where to find it ( same text as the product page )

        // the lit root and the dialog host side by side - a render never removes an open dialog
        this.root = document.createElement('div');
        this.wrapper = document.createElement('div');
        this.wrapper.className = 'cl-po--wrapper';
        el.appendChild(this.root);
        el.appendChild(this.wrapper);

        this.configs = SavedConfigs.standalone(settings, this.wrapper, () => this.render());
    }

    // every placeholder of the page once ( themes re-render the cart section )
    static mount(settings, shopifyCart) {
        if (ThemeConfig.cartSaveBtn) {
            CartSave.inject();
        }

        const elements = document.querySelectorAll('[data-live-options-cart-save]');
        for (let i = 0; i < elements.length; i++) {
            if (!elements[i]._clCartSave) {
                elements[i]._clCartSave = new CartSave(elements[i], settings, shopifyCart);
            }
            elements[i]._clCartSave.render();
        }
    }

    // no app block on the page > a placeholder above every checkout button ( cart page and drawer )
    // runs on every cart process: a drawer re-render removes the placeholder, the next process adds it again
    static inject() {
        if (document.querySelector('[data-live-options-cart-save]:not([data-live-options-cart-save="auto"])')) {
            return;
        }

        const buttons = document.querySelectorAll('[name="checkout"]:not(:disabled)');
        for (let i = 0; i < buttons.length; i++) {
            // the buttons container of Dawn / Horizon ( keeps the accelerated checkout below ), else the button itself
            const anchor = buttons[i].closest('.cart__ctas') || buttons[i];
            const previous = anchor.previousElementSibling;
            if (!previous || !previous.hasAttribute('data-live-options-cart-save')) {
                const el = document.createElement('div');
                el.dataset.liveOptionsCartSave = 'auto';
                anchor.parentNode.insertBefore(el, anchor);
            }
        }
    }

    // a price add-on line belongs to its configured line ( _cl_options_id without _cl_options ) - never saved on its own
    static saveable(line) {
        const properties = line.properties || {};
        return !properties[CartItemProperties.ID] || !!properties[CartItemProperties.OPTIONS];
    }

    // ConfigBody of a cart line - its properties are the source of the open and the cart restore, other apps' properties included
    static item(line) {
        const properties = SavedConfigs.lineProperties(line.properties || {});

        return {
            configUuid: properties[CartItemProperties.OPTIONS] || null, // null: a plain line
            productHandle: line.handle,
            productId: line.product_id,
            variantId: line.variant_id,
            quantity: line.quantity,
            properties: properties,
            previewUrl: properties[CartItemProperties.PREVIEW] || null
        };
    }

    // one save for the whole cart: an empty cart > nothing sent, an unidentified shopper is asked once, a failure saves nothing
    async save() {
        if (this.loading) {
            return;
        }

        this.loading = true;
        this.status = '';
        this.saved = false;
        this.render();

        try {
            const cart = await this.shopifyCart.getCart();
            const lines = (cart.items || []).filter(CartSave.saveable);

            if (!lines.length) {
                this.status = t('cart.empty', null, 'Nothing in your cart to save');
                return;
            }

            // closed, the login page or the feature is hidden > nothing to save
            if (!SavedConfigs.identity().signature && !(await this.configs.identify())) {
                return;
            }

            // in cart order - the last line is the newest configuration
            await this.configs.saveBatch(lines.map(CartSave.item));

            this.saved = true;
        } catch (error) {
            log.warn('[live-options] cart not saved', error);
            // 401 / 403 already reload or hide the feature
            if (error.status !== 401 && error.status !== 403) {
                this.status = t('error');
            }
        } finally {
            this.loading = false;
            this.render();
        }
    }

    render() {
        // no app embed marker or hidden for the visit
        if (!SavedConfigs.available()) {
            render(html``, this.root);
            return;
        }

        const data = this.el.dataset;
        const text = data.text || t('cart.save');
        const params = { btnIcon: data.icon, classList: data.classList };

        render(html`
            <div class="cl-po--cart-save ${classMap({ 'cl-po--loading': this.loading })}">
                <button type="button" class="cl-po--cart-save-button ${classMap(buttonClassMap(params, false))}" ?disabled=${this.loading} aria-busy="${this.loading ? 'true' : 'false'}"
                        @click=${() => this.save()}>${icons[params.btnIcon] || null}<span class="cl-po--cart-save-text">${text}</span>${this.loading ? html`<svg class="cl-po--icon loading" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" aria-hidden="true"><path d="M20 10c0 5.523-4.477 10-10 10S0 15.523 0 10 4.477 0 10 0v2c-4.418 0-8 3.582-8 8s3.582 8 8 8 8-3.582 8-8h2z"></path></svg>` : null}</button>
                <p class="cl-po--cart-save-status" role="status" aria-live="polite">${this.saved ? this.configs.savedNotice() : this.status}</p>
            </div>
        `, this.root);
    }
}

export default CartSave;
